<template>
  <div
    ref="root"
    class="relative max-w-full rounded-[0.3125rem] border-[0.5px] border-line [--plyr-color-main:var(--accent)]"
    :class="expanded ? 'w-full' : 'w-fit [--plyr-control-icon-size:0.875rem] [--plyr-control-spacing:0.5rem]'"
  >
    <video
      ref="videoElement"
      class="block h-auto! max-h-100 max-w-100 min-w-50 object-contain max-sm:max-w-full
             in-[.plyr:fullscreen]:h-full! in-[.plyr:fullscreen]:max-h-none! in-[.plyr:fullscreen]:w-full! in-[.plyr:fullscreen]:max-w-none!
             in-[.plyr--fullscreen-fallback]:h-full! in-[.plyr--fullscreen-fallback]:max-h-none!
             in-[.plyr--fullscreen-fallback]:w-full! in-[.plyr--fullscreen-fallback]:max-w-none!"
      :class="expanded ? 'max-h-[70vh]! w-full! max-w-none!' : metadataLoaded ? 'w-auto!' : 'aspect-video w-100!'"
      @loadedmetadata="metadataLoaded = true"
      preload="auto"
      :src="videoUrl"
      :type="videoType"
    ></video>
    <div
      v-if="!videoIsPlayable"
      class="absolute inset-0 flex flex-col items-center justify-center gap-5 bg-[radial-gradient(circle_at_center,#1f2330_0%,#0b0d13_100%)]
             before:size-13 before:animate-[spin_0.9s_linear_infinite] before:rounded-full before:border-3 before:border-white/12 before:border-t-[#4D96A3] before:content-['']"
    >
      <div class="text-group font-medium tracking-[0.0125rem] text-white/85">{{ $t('task.we_are_still_processing_video') }}...</div>
    </div>
    <div
      v-else-if="!isAllPlatformsAvailable && !dontCheckAlterPlatforms"
      class="group/convert absolute top-2 right-2 z-3 flex items-center gap-1.5 rounded-full bg-black/55 px-2 py-1 text-unit text-white/85 backdrop-blur-sm"
    >
      <IconLoader2 class="size-3 animate-spin" />
      {{ $t('task.converting_video') }}
      <span
        class="pointer-events-none absolute top-full right-0 w-56 translate-y-1 rounded-control bg-chrome p-2 text-label text-text opacity-0 transition-opacity duration-100
               group-hover/convert:opacity-100"
      >{{ $t('task.video_available_not_for_all_platforms') }}</span>
    </div>
    <template v-if="plyrSlots">
      <Teleport :to="plyrSlots.share">
        <button type="button" class="plyr__control" :disabled="!canShare" @click="copyVideoLink()">
          <IconLink16Solid />
          <span class="plyr__tooltip">{{ showVideoShare ? $t('task.copied_to_clipboard') : $t('task.copy_video_link') }}</span>
        </button>
      </Teleport>
      <Teleport :to="plyrSlots.expand">
        <button type="button" class="plyr__control" @click="toggleExpanded">
          <IconViewportNarrow v-if="expanded" />
          <IconViewportWide v-else />
          <span class="plyr__tooltip">{{ expanded ? $t('task.collapse_video') : $t('task.expand_video') }}</span>
        </button>
      </Teleport>
      <Teleport :to="plyrSlots.settingsButton">
        <IconEllipsisVertical16Solid />
      </Teleport>
      <Teleport :to="plyrSlots.menu">
        <label class="plyr__control cursor-default gap-3">
          <span>{{ $t('task.volume') }}</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            class="w-20"
            :value="volume"
            :style="{ '--value': `${volume * 100}%` }"
            @input="player.volume = Number($event.target.value)"
          >
        </label>
        <button v-if="pipSupported" type="button" class="plyr__control" @click="player.pip = !player.pip">
          <span>{{ $t('task.picture_in_picture') }}</span>
        </button>
        <a class="plyr__control" :href="videoUrl" download target="_blank">
          <span>{{ $t('invoices.download') }}</span>
        </a>
      </Teleport>
    </template>
  </div>
</template>
<script setup>
import {
  ref, computed, nextTick, onMounted, onBeforeUnmount,
} from 'vue';
import { IconEllipsisVertical16Solid, IconLink16Solid } from '@iconify-prerendered/vue-heroicons';
import { IconLoader2, IconViewportNarrow, IconViewportWide } from '@iconify-prerendered/vue-tabler';
import config from '@/config';

const props = defineProps({
  attachment: Object,
  href: String,
  videoType: String,
});

const videoElement = ref(null);
const plyrSlots = ref(null);
const volume = ref(1);
const metadataLoaded = ref(false);
const root = ref(null);
const expanded = ref(false);
const pipSupported = document.pictureInPictureEnabled;

const isScreenRecording = props.attachment.filename === 'video' && props.attachment.extension === 'mkv';

const setExpanded = async (value) => {
  expanded.value = value;
  await nextTick();
  root.value.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
};

const toggleExpanded = () => setExpanded(!expanded.value);
let player = null;

const isAllPlatformsAvailable = ref(false);
const dontCheckAlterPlatforms = ref(false);
const showVideoShare = ref(false);
const videoIsPlayable = ref(true);
const videoUrl = ref(props.href);
const alterVideoUrl = ref(props.href.replace('.webm', '.mp4').replace('.mkv', '.mp4'));
const timeDifferenceLimit = ref(12 * 60 * 60 * 1000); // This limitation is necessary because many videos were never converted to MP4.

let pollInterval = null;
let formatRequest = null;
let formatTimeout = null;

const shareLink = computed(() => {
  const domain = config.getSSRDomain();
  if (props.attachment.shortId) {
    return `${domain}/v/${props.attachment.shortId}`;
  }
  return `${domain}/video/${props.attachment.id}`;
});

const canShare = computed(() => Boolean(props.attachment.shortId) || Boolean(props.attachment.uploaded));

const appendCacheBuster = (url) => {
  const sep = url.includes('?') ? '&' : '?';
  return url + sep + 't=' + Date.now();
};

const pollForPlayableVideo = () => {
  const tryReload = () => {
    const cacheBustedUrl = appendCacheBuster(alterVideoUrl.value);
    videoUrl.value = cacheBustedUrl;
    nextTick(() => {
      const video = videoElement.value;
      if (video) {
        video.load();
      }
    });
  };
  if (pollInterval) clearInterval(pollInterval);
  pollInterval = setInterval(() => {
    tryReload();
  }, 3000);
};

const checkUrlExists = (url, callback) => {
  const xhr = new XMLHttpRequest();
  xhr.open('HEAD', url);
  xhr.onreadystatechange = () => {
    if (xhr.readyState === XMLHttpRequest.DONE) {
      formatRequest = null;
      callback(xhr.status === 200);
    }
  };
  xhr.send();
  return xhr;
};

const stopFormatCheck = () => {
  clearTimeout(formatTimeout);
  if (formatRequest) {
    formatRequest.onreadystatechange = null;
    formatRequest.abort();
    formatRequest = null;
  }
};

const checkAllFormats = () => {
  stopFormatCheck();
  // We currently only do MP4 conversion. If we add more formats, we should check them here.
  const currentDate = new Date();
  formatRequest = checkUrlExists(`${alterVideoUrl.value}?timestamp=${currentDate}`, (exists) => {
    if (exists) {
      isAllPlatformsAvailable.value = true;
    } else if (!props.attachment.createdAt || (currentDate - new Date(props.attachment.createdAt) < timeDifferenceLimit.value)) {
      formatTimeout = setTimeout(checkAllFormats, 10000);
    } else {
      dontCheckAlterPlatforms.value = true;
    }
  });
};

const copyVideoLink = () => {
  if (!canShare.value) return;

  const dummy = document.createElement('input');

  document.body.appendChild(dummy);
  dummy.value = shareLink.value;
  dummy.select();
  dummy.setSelectionRange(0, 99999); // For mobile devices
  document.execCommand('copy');
  document.body.removeChild(dummy);

  showVideoShare.value = !showVideoShare.value;
  setTimeout(() => { showVideoShare.value = !showVideoShare.value; }, 500);
};

const onVideoError = () => {
  videoIsPlayable.value = false;
  pollForPlayableVideo();
};

const onVideoCanPlay = () => {
  const video = videoElement.value;
  // Check if video track is present (not just audio)
  if (video.videoWidth === 0 || video.videoHeight === 0) {
    videoIsPlayable.value = false;
    pollForPlayableVideo();
  } else {
    checkAllFormats();
    videoIsPlayable.value = true;
    if (pollInterval) clearInterval(pollInterval);
    pollInterval = null;
  }
};

onMounted(() => {
  videoElement.value.addEventListener('error', onVideoError);
  videoElement.value.addEventListener('canplay', onVideoCanPlay);

  Promise.all([import('plyr'), import('plyr/dist/plyr.css')]).then(([{ default: Plyr }]) => {
    player = new Plyr(videoElement.value, {
      controls: ['play-large', 'play', 'progress', 'current-time', 'settings', 'fullscreen'],
      settings: ['speed'],
    });
    if (isScreenRecording) {
      player.on('play', () => {
        if (!expanded.value) setExpanded(true);
      });
      player.on('ended', () => {
        if (expanded.value) setExpanded(false);
      });
    }
    player.on('volumechange', () => {
      volume.value = player.muted ? 0 : player.volume;
    });
    player.on('ready', () => {
      volume.value = player.muted ? 0 : player.volume;
      const [share, expand] = ['share', 'expand'].map(() => {
        const slot = document.createElement('div');
        slot.className = 'plyr__controls__item';
        player.elements.controls.insertBefore(slot, player.elements.buttons.fullscreen);
        return slot;
      });
      plyrSlots.value = {
        share,
        expand,
        settingsButton: player.elements.buttons.settings,
        menu: player.elements.settings.panels.home.querySelector('[role=menu]'),
      };
    });
  });
});

onBeforeUnmount(() => {
  player?.destroy();
  videoElement.value.removeEventListener('error', onVideoError);
  videoElement.value.removeEventListener('canplay', onVideoCanPlay);
  stopFormatCheck();
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
});
</script>

<style scoped>
:deep(.plyr__control[data-plyr='settings'] > svg:first-child) {
  display: none;
}

</style>
